import React, { useState, useContext, useEffect } from 'react';
import { 
  Box, 
  Avatar, 
  Typography, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText, 
  alpha, 
  useTheme,
  Tooltip
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { MdLogout, MdSettings, MdPerson, MdMoreVert } from 'react-icons/md';
import { AuthContext } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axiosInstance';
import { DonorUserResponse } from '../../../typings/users';
import { getRoleName } from '../../../data';

interface SideBarProfileProps {
  isOpen: boolean;
}

export const SideBarProfile: React.FC<SideBarProfileProps> = ({ isOpen }) => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userData, setUserData] = useState<DonorUserResponse | null>(null);

  const open = Boolean(anchorEl);
  const { colors } = theme.palette;

  useEffect(() => {
    const fetchUser = async () => {
      if (!auth?.userId) return;
      try {
        const response = await api.get(`/users/${auth.userId}/`);
        if (response.data?.data) {
          setUserData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUser();
  }, [auth?.userId]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    auth?.logout();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 1);
  };

  return (
    <Box sx={{ width: '100%', mt: 'auto', pt: 1 }}>
      <Tooltip title={!isOpen ? (userData?.name || 'Profile') : ''} placement="right">
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            margin: '4px 0',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: open ? colors['white']['50'] : 'transparent',
            '&:hover': {
              backgroundColor: colors['white']['50'],
            },
            minHeight: '44px',
            justifyContent: isOpen ? 'flex-start' : 'center',
            gap: isOpen ? '12px' : '0',
          }}
        >
          <Avatar
            src={userData?.profile_picture_url || undefined}
            sx={{
              width: 20,
              height: 20,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              flexShrink: 0,
            }}
          >
            {!userData?.profile_picture_url && (userData?.name ? getInitials(userData.name) : getInitials(auth?.role || 'User'))}
          </Avatar>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  flexGrow: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: '13px',
                    color: colors['grayGreen']['600'],
                    lineHeight: 1.2,
                  }}
                >
                  {userData?.name || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    color: colors['grayGreen']['400'],
                    fontWeight: 500,
                  }}
                >
                  {getRoleName(userData?.role || auth?.role || '')}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>

          {isOpen && (
            <MdMoreVert 
              size={18} 
              color={colors['grayGreen']['400']} 
              style={{ flexShrink: 0, marginLeft: 'auto' }} 
            />
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          elevation: 4,
          sx: {
            ml: isOpen ? 0 : 5,
            mt: -1,
            minWidth: 180,
            borderRadius: 2,
            border: `1px solid ${colors["gray"]["200"]}`,
            '& .MuiMenuItem-root': {
              fontSize: '13px',
              padding: '8px 16px',
              borderRadius: '6px',
              margin: '2px 4px',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors["gray"]["100"]}`, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors["gray"]["900"], fontSize: '13px' }}>
            {userData?.name}
          </Typography>
          <Typography variant="caption" sx={{ color: colors["gray"]["500"], display: 'block', fontSize: '11px' }}>
            {userData?.email}
          </Typography>
        </Box>
        
        <MenuItem>
          <ListItemIcon>
            <MdPerson size={18} color={colors["gray"]["600"]} />
          </ListItemIcon>
          <ListItemText primary="View Profile" primaryTypographyProps={{ fontSize: '13px' }} />
        </MenuItem>
        
        <MenuItem>
          <ListItemIcon>
            <MdSettings size={18} color={colors["gray"]["600"]} />
          </ListItemIcon>
          <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '13px' }} />
        </MenuItem>

        <MenuItem 
          onClick={handleLogout}
          sx={{ 
            color: 'error.main', 
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }
          }}
        >
          <ListItemIcon sx={{ color: 'error.main' }}>
            <MdLogout size={18} />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

